import { shownShortVolume } from 'src/tv/dashboardFormat';
import type { KhDashboard, KhHandlerRow, KhTotals, MbDashboard, MbSellerRow, MbTotals, TvDashboardSnapshot } from 'src/tv/dashboardTypes';

/**
 * Snapshot → what the screens draw. The snapshot is already aggregated server-side; this only
 * derives ratios, cuts the rows to what fits one 1080p screen and marks the chart's days.
 * zrm decides which seller and handler rows appear and their order; they are shown as sent.
 */

/** Rows a 1080p screen holds without shrinking the type below what reads across a room. */
export const MB_MAX_ROWS = 20;
export const KH_MAX_ROWS = 14;

export interface GoalView {
  result: number | null;
  goal: number | null;
  /** Share of the goal reached, capped at 1; null without a goal. */
  progress: number | null;
  isMet: boolean;
}

export const goalView = (result: number | null, goal: number | null): GoalView => {
  const hasGoal = goal !== null && goal > 0 && result !== null;

  return {
    result,
    goal,
    progress: hasGoal ? Math.min(1, result / goal) : null,
    // Judged on the number the screen draws, so a shown "1 100 / goal 1 100" is never left unmet.
    isMet: hasGoal && Math.round(result) >= goal,
  };
};

export interface PlanRow {
  goal: number | null;
  runrate: number | null;
  /** Reached as the screen shows both numbers, so a runrate drawn equal to its goal is never left unmet. */
  isMet: boolean;
}

/** A runrate against its goal, compared after `shown` rounds both to the precision the screen draws. */
export const planRow = (runrate: number | null, goal: number | null, shown: (value: number) => number): PlanRow => ({
  goal,
  runrate,
  isMet: runrate !== null && goal !== null && goal > 0 && shown(runrate) >= shown(goal),
});

/** `a / b`, or null when there is nothing to divide by. */
export const ratio = (numerator: number | null, denominator: number | null): number | null => (
  numerator !== null && denominator ? numerator / denominator : null
);

export interface WeekdayBar {
  date: string;
  apps: number;
  isToday: boolean;
  /** Still to come this week: drawn as an empty slot, not a zero. */
  isAhead: boolean;
}

export interface MbView {
  day: GoalView;
  week: GoalView;
  /** Runrate against the month goal; `soFar` is the month-to-date count behind it. */
  month: GoalView & { soFar: number };
  today: MbTotals;
  weekdays: WeekdayBar[];
  /** Top of the column chart's scale: the higher of the best day and the daily goal. */
  weekdayScale: number;
  dayGoal: number | null;
  /** Counts and `formatKr` are whole numbers, the volume `formatShortVolume`'s 0,1 million. */
  plan: { helped: PlanRow; averageLoan: PlanRow; volume: PlanRow };
  sellers: MbSellerRow[];
  /** Sellers that did not fit. */
  hiddenSellers: number;
  totals: { today: MbTotals; month: MbTotals; returnRate: number | null };
}

export const mbView = (snapshot: TvDashboardSnapshot, mb: MbDashboard): MbView => {
  const { goals, totals, runrate } = mb;
  const weekdays = mb.apps_by_weekday.map(({ date, apps }) => ({
    date,
    apps,
    isToday: date === snapshot.today,
    isAhead: date > snapshot.today,
  }));

  return {
    day: goalView(totals.today.apps, goals.apps_day),
    week: goalView(totals.week.apps, goals.apps_week),
    month: { ...goalView(runrate.apps, goals.apps_month), soFar: totals.month.apps },
    today: totals.today,
    weekdays,
    weekdayScale: Math.max(1, goals.apps_day ?? 0, ...weekdays.map(({ apps }) => apps)),
    dayGoal: goals.apps_day,
    plan: {
      helped: planRow(runrate.helped, goals.helped_month, Math.round),
      averageLoan: planRow(ratio(runrate.helped_volume, runrate.helped), ratio(goals.helped_volume_month, goals.helped_month), Math.round),
      volume: planRow(runrate.helped_volume, goals.helped_volume_month, shownShortVolume),
    },
    sellers: mb.sellers.slice(0, MB_MAX_ROWS),
    hiddenSellers: Math.max(0, mb.sellers.length - MB_MAX_ROWS),
    totals: {
      today: totals.today,
      month: totals.month,
      returnRate: ratio(totals.month.helped, totals.month.utskick),
    },
  };
};

export interface KhView {
  totals: { today: KhTotals; week: KhTotals; month: KhTotals };
  handlers: KhHandlerRow[];
  hiddenHandlers: number;
}

export const khView = (kh: KhDashboard): KhView => ({
  totals: kh.totals,
  handlers: kh.handlers.slice(0, KH_MAX_ROWS),
  hiddenHandlers: Math.max(0, kh.handlers.length - KH_MAX_ROWS),
});
